// src/component/admin/support/ProductReviewsTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  SearchIcon,
  ArrowDown,
  StarIcon,
  FilterIcon,
  CloseIcon,
  CautionIcon,
  CheckCircleIcon,
  EmailIcon
} from '../../../public/icons';
import Pagination from '../../shared/Pagination';
import ReviewDetailsModal from '../review/ReviewDetailsModal';
import adminReviewService from '../../../services/admin/admin-reviewService';
import { useToast } from '../../../context/ToastContext';

/* ─────────────────────────── Mobile Filters Modal ─────────────────────────── */
const MobileFiltersModal = ({
  isOpen,
  onClose,
  searchQuery,
  setSearchQuery,
  statusFilter,
  setStatusFilter,
  ratingFilter,
  setRatingFilter,
  statusFilters,
  ratingFilters
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-50 p-5 max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-lg font-medium text-[#1C2A46]">Filters</h3>
          <button onClick={onClose} className="p-1">
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search by reviewer, product, content..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#D4A574] text-sm"
              />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="grid grid-cols-2 gap-2">
              {statusFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    statusFilter === f.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
            <div className="grid grid-cols-3 gap-2">
              {ratingFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setRatingFilter(f.id)}
                  className={`px-3 py-2 text-sm rounded border transition-colors ${
                    ratingFilter === f.id
                      ? 'bg-[#D2A146] text-white border-[#D2A146]'
                      : 'bg-white text-[#1C2A46] border-[#DBDBDB] hover:border-[#D2A146]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={() => {
              setSearchQuery('');
              setStatusFilter('all');
              setRatingFilter('all');
              onClose();
            }}
            className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded hover:bg-gray-50 transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-[#D2A146] text-white font-medium rounded hover:bg-[#C49660] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
};

/* ─────────────────────────── Mobile Review Card ─────────────────────────── */
const MobileReviewCard = ({ review, onView }) => {
  const statusCfg = adminReviewService.getStatusBadgeConfig(review.status);

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3">
      {/* Header */}
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 text-base truncate">
            {review.reviewerName || 'Anonymous'}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">
            {review.reviewerEmail}
          </div>
        </div>
        <span
          className={`flex items-center gap-1.5 px-2 py-1 text-xs font-semibold rounded-full ${statusCfg.bgColor} flex-shrink-0`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${statusCfg.dotColor}`} />
          <span className={statusCfg.textColor}>{review.status}</span>
        </span>
      </div>

      {/* Stars */}
      <div className="mt-3 flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <StarIcon
            key={s}
            className={`w-4 h-4 ${
              s <= review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
            }`}
          />
        ))}
        <span className="text-xs text-gray-500 ml-1">{review.rating}/5</span>
      </div>

      {/* Product */}
      <div className="mt-3 text-sm text-gray-700">
        <span className="text-xs text-gray-500 block mb-1">Product:</span>
        <span className="font-medium">
          {review.productSnapshot?.title || 'N/A'}
        </span>
      </div>

      {/* Review content */}
      <div className="mt-2 text-sm text-gray-700">
        {review.title && (
          <div className="font-medium text-[#0E1628] mb-0.5">{review.title}</div>
        )}
        <p className="text-xs text-gray-500 line-clamp-2">{review.text}</p>
      </div>

      {/* Date */}
      <div className="mt-2 text-xs text-gray-500">
        {adminReviewService.formatDate(review.createdAt)}
      </div>

      <button
        onClick={() => onView(review)}
        className="mt-3 w-full py-2 text-center text-sm text-secondary hover:text-[#C49660] border-t border-gray-100 pt-3"
      >
        View Details
      </button>
    </div>
  );
};

/* ─────────────────────────── Main Tab Component ─────────────────────────── */
const ProductReviewsTab = () => {
  const { success, error: showError } = useToast();

  // Data
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Detail modal
  const [selectedReview, setSelectedReview] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    totalReviews: 0,
    pendingReviews: 0,
    approvedReviews: 0,
    rejectedReviews: 0,
    averageRating: 0
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    totalItems: 0
  });

  const statusFilters = [
    { id: 'all', label: 'All status' },
    { id: 'pending', label: 'Pending' },
    { id: 'approved', label: 'Approved' },
    { id: 'rejected', label: 'Rejected' },
    { id: 'spam', label: 'Spam' }
  ];

  const ratingFilters = [
    { id: 'all', label: 'All' },
    { id: '5', label: '5★' },
    { id: '4', label: '4★' },
    { id: '3', label: '3★' },
    { id: '2', label: '2★' },
    { id: '1', label: '1★' }
  ];

  /* ───────── Fetch reviews ───────── */
  const fetchReviews = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const params = {
          page,
          limit: pagination.itemsPerPage,
          search: searchQuery || undefined,
          status: statusFilter !== 'all' ? statusFilter : undefined,
          rating: ratingFilter !== 'all' ? ratingFilter : undefined
        };

        const response = await adminReviewService.getReviews(params);

        if (response.success) {
          setReviews(response.data.reviews || []);
          setPagination((prev) => ({
            ...prev,
            currentPage: response.data.pagination.page,
            totalPages: response.data.pagination.pages,
            totalItems: response.data.pagination.total
          }));
        } else {
          setReviews([]);
        }
      } catch (err) {
        console.error('Fetch reviews error:', err);
        showError('Failed to load reviews');
        setReviews([]);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, statusFilter, ratingFilter, pagination.itemsPerPage, showError]
  );

  /* ───────── Fetch stats ───────── */
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await adminReviewService.getReviewStats();
      if (response.success) setStats(response.data);
    } catch (err) {
      console.error('Fetch stats error:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews(pagination.currentPage);
  }, [fetchReviews, pagination.currentPage]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  /* ───────── Debounced filter changes ───────── */
  useEffect(() => {
    const t = setTimeout(() => {
      if (pagination.currentPage === 1) fetchReviews(1);
      else setPagination((prev) => ({ ...prev, currentPage: 1 }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery, statusFilter, ratingFilter, fetchReviews]);

  /* ───────── Handlers ───────── */
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const handleView = (review) => {
    setSelectedReview(review);
    setDetailsOpen(true);
  };

  const handleModerate = async (id, status, note) => {
    try {
      const res = await adminReviewService.moderateReview(id, status, note);
      if (res.success) {
        success(`Review ${status}`);
        fetchReviews(pagination.currentPage);
        fetchStats();
      }
    } catch (err) {
      console.error(err);
      showError('Failed to moderate review');
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await adminReviewService.deleteReview(id);
      if (res.success) {
        success('Review deleted');
        fetchReviews(pagination.currentPage);
        fetchStats();
      }
    } catch (err) {
      console.error(err);
      showError('Failed to delete review');
    }
  };

  /* ───────── Status badge (desktop) ───────── */
  const getStatusBadge = (status) => {
    const cfg = adminReviewService.getStatusBadgeConfig(status);
    return (
      <span
        className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 text-xs leading-5 font-semibold rounded-full ${cfg.bgColor} w-fit`}
      >
        <span className={`h-2 w-2 rounded-full ${cfg.dotColor}`} />
        <span className={cfg.textColor}>{status}</span>
        <ArrowDown size={12} className={cfg.textColor} />
      </span>
    );
  };

  /* ───────── Stats cards ───────── */
  const statsCards = [
    {
      label: 'Total Reviews',
      value: stats.totalReviews,
      icon: <EmailIcon color="#2F578F" />,
      color: 'bg-[#E7ECF4] text-[#2F578F]'
    },
    {
      label: 'Pending',
      value: stats.pendingReviews,
      icon: <CautionIcon color="#D8A100" />,
      color: 'bg-[#FFF4D6] text-[#D8A100]'
    },
    {
      label: 'Approved',
      value: stats.approvedReviews,
      icon: <CheckCircleIcon color="#2F8F5B" />,
      color: 'bg-[#E7F4ED] text-[#2F8F5B]'
    },
    {
      label: 'Avg Rating',
      value:
        stats.averageRating != null
          ? Number(stats.averageRating).toFixed(1)
          : '0.0',
      icon: <StarIcon color="#8F7F2F" />,
      color: 'bg-[#F4F2E7] text-[#8F7F2F]'
    }
  ];

  return (
    <div className="px-2 sm:px-0">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8">
        {statsCards.map((stat, i) => (
          <div key={i} className="bg-white rounded-sm p-3 sm:p-4 md:p-5 shadow-sm">
            <div className="text-left">
              <div
                className={`flex justify-center items-center rounded-md h-10 w-10 sm:h-12 sm:w-12 ${stat.color}`}
              >
                {stat.icon}
              </div>
              <div>
                <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1C2A46] mt-2 sm:mt-4">
                  {statsLoading ? (
                    <span className="inline-block w-6 h-6 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    stat.value
                  )}
                </p>
                <p className="text-xs sm:text-sm text-[#6B635D] mt-0.5 sm:mt-1">
                  {stat.label}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reviews Card */}
      <div className="bg-white rounded-sm shadow-sm">
        {/* Header */}
        <div className="mb-4 sm:mb-6 p-4 sm:p-6 border-b border-[#DBDBDB]">
          {/* Desktop */}
          <div className="hidden lg:flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-medium text-[#0E1628]">
              All Product Reviews
            </h2>

            <div className="flex gap-3 lg:gap-4 items-center">
              {/* Search */}
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <SearchIcon size={18} className="text-[#6B635D]" />
                </div>
                <input
                  type="text"
                  placeholder="Search reviews..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="p-2 pl-9 pr-3 border w-64 lg:w-80 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs lg:placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                />
              </div>

              {/* Status filter */}
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="p-2 pl-3 pr-7 border w-32 lg:w-36 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                >
                  {statusFilters.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.label}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ArrowDown size={14} className="text-[#6B635D]" />
                </div>
              </div>

              {/* Rating filter */}
              <div className="relative">
                <select
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  className="p-2 pl-3 pr-7 border w-28 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-xs lg:text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
                >
                  {ratingFilters.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.id === 'all' ? 'All ratings' : `${f.id} stars`}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ArrowDown size={14} className="text-[#6B635D]" />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-medium text-[#0E1628]">
                All Product Reviews
              </h2>
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="flex items-center gap-2 px-3 py-2 border border-[#DBDBDB] rounded-sm text-sm"
              >
                <FilterIcon size={14} />
                Filters
              </button>
            </div>

            {/* Mobile search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                <SearchIcon size={16} className="text-[#6B635D]" />
              </div>
              <input
                type="text"
                placeholder="Search reviews..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-2.5 pl-10 pr-3 border border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-xs focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent rounded"
              />
            </div>
          </div>
        </div>

        {/* Desktop Table */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-[#DBDBDB]">
            <thead className="bg-[#F3F4F6]">
              <tr>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Reviewer
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Product
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Rating
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Review
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Date
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider">
                  Status
                </th>
                <th className="px-3 sm:px-4 md:px-6 py-3 text-left text-[10px] sm:text-xs font-normal text-[#6B635D] uppercase tracking-wider w-20">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#DBDBDB]">
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#D2A146] mx-auto" />
                    <p className="mt-3 text-sm text-gray-600">Loading reviews…</p>
                  </td>
                </tr>
              ) : reviews.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-sm text-gray-500">
                    No reviews found
                  </td>
                </tr>
              ) : (
                reviews.map((review) => (
                  <tr
                    key={review._id}
                    className="hover:bg-gray-50 transition-colors duration-150 text-left"
                  >
                    {/* Reviewer */}
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                      <div className="flex flex-col">
                        <span className="text-xs sm:text-sm font-normal text-[#0E1628]">
                          {review.reviewerName || 'Anonymous'}
                        </span>
                        <span className="text-[10px] sm:text-xs text-[#6B635D] truncate max-w-[180px]">
                          {review.reviewerEmail}
                        </span>
                      </div>
                    </td>

                    {/* Product */}
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4">
                      <div className="flex items-center gap-2">
                        {review.productSnapshot?.image && (
                          <img
                            src={review.productSnapshot.image}
                            alt={review.productSnapshot?.title || ''}
                            className="w-8 h-8 sm:w-9 sm:h-9 object-cover rounded flex-shrink-0"
                          />
                        )}
                        <span className="text-xs sm:text-sm text-[#0E1628] line-clamp-2 max-w-[180px]">
                          {review.productSnapshot?.title || 'N/A'}
                        </span>
                      </div>
                    </td>

                    {/* Rating */}
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((s) => (
                          <StarIcon
                            key={s}
                            className={`w-3 h-3 sm:w-4 sm:h-4 ${
                              s <= review.rating
                                ? 'text-yellow-400 fill-current'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="text-[10px] sm:text-xs text-[#6B635D] ml-1">
                          {review.rating}
                        </span>
                      </div>
                    </td>

                    {/* Review text */}
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 max-w-[260px]">
                      {review.title && (
                        <div className="text-xs sm:text-sm font-medium text-[#0E1628] mb-0.5 truncate">
                          {review.title}
                        </div>
                      )}
                      <div className="text-xs text-[#6B635D] line-clamp-2">
                        {review.text}
                      </div>
                    </td>

                    {/* Date */}
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className="text-xs sm:text-sm text-[#8A9099]">
                        {adminReviewService.formatDate(review.createdAt)}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap">
                      {getStatusBadge(review.status)}
                    </td>

                    {/* Action */}
                    <td className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 whitespace-nowrap w-20">
                      <button
                        onClick={() => handleView(review)}
                        className="text-xs sm:text-sm font-normal text-secondary hover:text-[#C49660] transition-colors"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden px-4 pb-4">
          {loading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D2A146] mx-auto" />
              <p className="mt-2 text-sm text-gray-500">Loading…</p>
            </div>
          ) : reviews.length > 0 ? (
            reviews.map((review) => (
              <MobileReviewCard
                key={review._id}
                review={review}
                onView={handleView}
              />
            ))
          ) : (
            <div className="text-center py-10">
              <svg
                className="mx-auto h-10 w-10 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118L2.075 10.1c-.784-.57-.38-1.81.588-1.81h4.915a1 1 0 00.95-.69l1.52-4.674z"
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">
                No reviews found matching your criteria
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && !loading && (
          <div className="border-t border-[#DBDBDB] px-4 sm:px-6 py-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs text-[#6B635D] order-2 sm:order-1">
                Showing {reviews.length} out of {pagination.totalItems} reviews
              </div>
              <div className="order-1 sm:order-2 w-full sm:w-auto">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <ReviewDetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        review={selectedReview}
        onModerate={handleModerate}
        onDelete={handleDelete}
      />

      {/* Mobile Filters */}
      <MobileFiltersModal
        isOpen={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        ratingFilter={ratingFilter}
        setRatingFilter={setRatingFilter}
        statusFilters={statusFilters}
        ratingFilters={ratingFilters}
      />
    </div>
  );
};

export default ProductReviewsTab;